import type * as React from 'react';

import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch, useHiveRevision } from '@gitbook/hive-react';
import { DropLine } from '@gitbook/react-uikit';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { type DNDItem, useContentNavigation, useDroppableWithCallback } from '../../../Components';

/**
 * Line to insert page in the TOC.
 */
export function ToCInsertionLine(props: {
    page: hiveCore.RevisionPage;
    position: 'after' | 'before';
    visible: boolean;
}): React.ReactElement {
    const { page, position, visible } = props;

    const { navigateToPage } = useContentNavigation();
    const hiveBranch = useHiveBranch();
    const { sourceRevision } = useHiveRevision();

    //
    // Drop element in the page
    //
    const drop = useDroppableWithCallback({
        shouldAccept: (item) => {
            if (item.type === 'page') {
                return hiveCore.isPageMoveableInRevisionNextTo(sourceRevision, item.id, page.id);
            }

            return false;
        },

        onDrop: (item: DNDItem) => {
            assertIsDefined(hiveBranch);

            if (item.type === 'page') {
                hiveBranch.change((change) => {
                    switch (position) {
                        case 'before': {
                            change.movePageBefore(item.id, page.id);
                            break;
                        }
                        case 'after': {
                            change.movePageAfter(item.id, page.id);
                            break;
                        }
                        default:
                            assertNever(position);
                    }
                });
            }
        },
    });

    //
    // Insert page when pressing button
    //
    const onInsertPage = () => {
        assertIsDefined(hiveBranch);

        const newPage = hiveCore.createPageDocument({
            title: hiveCore.getUniquePageTitle(hiveBranch.revision),
        });

        hiveBranch.change((change) => {
            switch (position) {
                case 'before': {
                    change.insertPageBefore(newPage, page.id);
                    break;
                }
                case 'after': {
                    // When inserting "after" a group, in fact we insert as a first child of the group
                    if (page.type === hiveCore.RevisionPageType.Group) {
                        change.insertPage(newPage, {
                            into: page.id,
                            at: 0,
                        });
                    } else {
                        change.insertPageAfter(newPage, page.id);
                    }
                    break;
                }
                default:
                    assertNever(position);
            }
        });

        navigateToPage(hiveBranch.revision, newPage.id);
    };

    return (
        <DropLine
            ref={drop.setNodeRef}
            dropHovered={drop.isOver}
            buttonVisible={visible}
            position={(() => {
                switch (position) {
                    case 'before':
                        return 'top';
                    case 'after':
                        return 'bottom';
                    default:
                        assertNever(position);
                }
            })()}
            buttonAlign="end"
            insertButton={{
                onPress: onInsertPage,
                label: 'Insert page',
            }}
        />
    );
}
